import { BusinessAutomationActionTypeEnum, BusinessAutomationTriggerTypeEnum, dateBasedTriggerMetadataSchema, eventBasedTriggerMetadataSchema, oneTimeTriggerMetadataSchema, ReputationTemplateTypeEnum, } from '@reach/shared-types/business-automation';
import { dynamicMergeFieldSchema, staticMergeFieldSchema, } from '@reach/shared-types/communication-groups';
import { BusinessSegmentConditionsSchema } from '@reach/shared-types/conditions';
import { automationCategoryResponseSchema } from '@reach/shared-types/automation-category';
import { ReachFeature } from '@reach/shared-types/features';
import { ianaTimezoneSchema } from '@reach/shared-types/us-iana-timezone-options';
import { z } from 'zod';
export { ReputationTemplateTypeEnum } from './business-automation.js';
export var AutomationTemplateSegmentTypeEnum;
(function (AutomationTemplateSegmentTypeEnum) {
    AutomationTemplateSegmentTypeEnum["ALL_USERS"] = "all_users";
})(AutomationTemplateSegmentTypeEnum || (AutomationTemplateSegmentTypeEnum = {}));
export var AutomationTemplateVisibilityEnum;
(function (AutomationTemplateVisibilityEnum) {
    AutomationTemplateVisibilityEnum["VISIBLE"] = "visible";
    AutomationTemplateVisibilityEnum["HIDDEN"] = "hidden";
})(AutomationTemplateVisibilityEnum || (AutomationTemplateVisibilityEnum = {}));
/**
 * Reputation-specific sub-types. Discriminates the role a reputation
 * automation plays in the tenant's selected review flow.
 */
export const ReputationFeatureSubTypeEnum = {
    INTERNAL_REVIEW: 'internal-review',
    GOOGLE_REVIEW: 'google-review',
    POST_INTERNAL_REVIEW: 'post-internal-review',
    BOTH_REVIEWS: 'both-reviews',
};
export const reputationFeatureSubTypeSchema = z.enum(Object.values(ReputationFeatureSubTypeEnum));
/**
 * Zod schema for the automation template feature_sub_type column.
 */
export const automationFeatureSubTypeSchema = reputationFeatureSubTypeSchema.nullable();
/**
 * What each reputation sub-type asks the customer for. Shared so the selection UI, the customer
 * preview and the activation path cannot disagree about what a sub-type means.
 *
 * Each record is exhaustive, so a new sub-type is a compile error until its behaviour is declared.
 */
export const SUBTYPE_ASKS_PUBLIC_REVIEW = {
    [ReputationFeatureSubTypeEnum.INTERNAL_REVIEW]: false,
    [ReputationFeatureSubTypeEnum.GOOGLE_REVIEW]: true,
    [ReputationFeatureSubTypeEnum.POST_INTERNAL_REVIEW]: true,
    [ReputationFeatureSubTypeEnum.BOTH_REVIEWS]: true,
};
export const SUBTYPE_ASKS_PRIVATE_FEEDBACK = {
    [ReputationFeatureSubTypeEnum.INTERNAL_REVIEW]: true,
    [ReputationFeatureSubTypeEnum.GOOGLE_REVIEW]: false,
    [ReputationFeatureSubTypeEnum.POST_INTERNAL_REVIEW]: false,
    [ReputationFeatureSubTypeEnum.BOTH_REVIEWS]: true,
};
/** Whether a set of activated sub-types puts a public review ask in front of the customer. */
export const subTypesAskForPublicReview = (subTypes) => subTypes.some(subType => SUBTYPE_ASKS_PUBLIC_REVIEW[subType]);
/** Whether a set of activated sub-types collects private feedback through a form. */
export const subTypesAskForPrivateFeedback = (subTypes) => subTypes.some(subType => SUBTYPE_ASKS_PRIVATE_FEEDBACK[subType]);
const extraMergeFieldsSchemaForTemplates = z.array(z.union([staticMergeFieldSchema, dynamicMergeFieldSchema]));
const segmentDefault = z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    conditions: BusinessSegmentConditionsSchema,
});
const segmentOptions = z.array(z.union([
    segmentDefault,
    z.literal(AutomationTemplateSegmentTypeEnum.ALL_USERS),
    z.string().uuid().describe('Partner segment definition ID'),
]));
const baseTemplateActionSchema = z.object({
    templateActionId: z
        .guid()
        .optional()
        .describe("Stable ID of this step. Sync matches steps by this ID, so tenants' automations keep their existing step (and, for messages, its communication group) when it is unchanged. Omit it to add a new step; the server assigns one."),
    index: z.number().describe('Position of the step. Steps run in ascending index order.'),
});
export const automationTemplateContents = z.object({
    name: z.string(),
    description: z.string(),
    feature: z.nativeEnum(ReachFeature).optional(),
    triggerType: z.nativeEnum(BusinessAutomationTriggerTypeEnum),
    /**
     * @see business-automation.types.ts
     */
    triggerMetadata: z.discriminatedUnion('triggerType', [
        oneTimeTriggerMetadataSchema.extend({
            shouldOverrideActiveAutomations: z
                .boolean()
                .optional()
                .describe('If true, even automations that are marked active will sync the trigger metadata (e.g. even if a user turned it on and it says Jan 1, if you update + sync to Jan 2, this will override the active automation and set the date to Jan 2)'),
        }),
        /**
         * We dont need the rootActionId for the template, that'll be
         * filled in when we build the automation
         */
        eventBasedTriggerMetadataSchema.omit({ rootActionId: true }).extend({
            shouldOverrideActiveAutomations: z
                .boolean()
                .optional()
                .describe('If true, even automations that are marked active will sync the trigger metadata (e.g. even if a user turned it on and it says Jan 1, if you update + sync to Jan 2, this will override the active automation and set the date to Jan 2)'),
        }),
        dateBasedTriggerMetadataSchema.omit({ rootActionId: true }).extend({
            shouldOverrideActiveAutomations: z
                .boolean()
                .optional()
                .describe('If true, even automations that are marked active will sync the date (e.g. even if a user turned it on and it says Jan 1, if you update + sync to Jan 2, this will override the active automation and set the date to Jan 2)'),
        }),
    ]),
    actionData: z.array(z.discriminatedUnion('type', [
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.SEND_COMMUNICATION),
            html: z.string(),
            css: z.string(),
            /**
             * Compiled html to use if the user has not updated the html above
             */
            compiledHtml: z.string(),
            text: z.string(),
            subject: z.string(),
            previewText: z.string(),
            extraMergeFields: extraMergeFieldsSchemaForTemplates.optional(),
            /**
             * Optional form template keys to reference
             * These reference form templates by their template_key
             * Forms will be synced from form templates before automation sync
             */
            formTemplateKeys: z.array(z.string()).optional(),
            customBlockText: z.string().optional(),
            /**
             * Specifies the type of reputation communication (e.g., feedback-request, google-review)
             */
            reputationTemplateType: z.nativeEnum(ReputationTemplateTypeEnum).optional(),
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_AT_LEAST_NEXT_AVAILABLE_TIME),
            hour: z.number(),
            minute: z.number(),
            timezone: ianaTimezoneSchema,
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.WAIT_UNTIL_NEXT_OCCURRENCE),
            hour: z.number(),
            minute: z.number(),
            timezone: ianaTimezoneSchema,
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.WAIT_FOR_DURATION),
            days: z.number().min(0).default(0),
            hours: z.number().min(0).default(0),
            minutes: z.number().min(0).default(0),
            seconds: z.number().min(0).default(0),
        }),
        baseTemplateActionSchema.extend({
            type: z.literal(BusinessAutomationActionTypeEnum.CHECK_SEGMENT_MEMBERSHIP),
            segmentIds: z.array(z.string()),
        }),
    ])),
    includeSegmentDefaults: segmentOptions,
    excludeSegmentDefaults: segmentOptions.optional(),
    /**
     * The priority of the automation template, this is used to order managed automations
     * when they are synced to businesses
     */
    priority: z.number().optional(),
    /**
     * Template default for whether tenant automations should use the point-of-contact sender when the partner allows it.
     * Omitted/undefined is stored as unset; only explicit true should mean opt-in once send-time behavior exists.
     */
    usePointOfContactSender: z.boolean().optional(),
    /**
     * The `profileId` of the selected sender profile from
     * partner_schema_mappings.contactsSchema.primarySource.senderProfiles.
     * null = no profile selected; undefined = field unset (legacy row).
     */
    senderProfileId: z.string().uuid().nullable().optional(),
});
export var AutomationTemplateTenantsModeEnum;
(function (AutomationTemplateTenantsModeEnum) {
    AutomationTemplateTenantsModeEnum["ALL"] = "all";
    AutomationTemplateTenantsModeEnum["SELECTED"] = "selected";
})(AutomationTemplateTenantsModeEnum || (AutomationTemplateTenantsModeEnum = {}));
export const automationTemplateTenantsSchema = z
    .discriminatedUnion('mode', [
    z.object({
        mode: z.literal(AutomationTemplateTenantsModeEnum.ALL),
        excludedTenantIds: z
            .array(z.string().uuid())
            .describe('Tenants left out. Tenants created later still receive the template.'),
    }),
    z.object({
        mode: z.literal(AutomationTemplateTenantsModeEnum.SELECTED),
        tenantIds: z
            .array(z.string().uuid())
            .describe('The only tenants that receive the template. [] = none (draft).'),
    }),
])
    .describe('Tenants the template is synced to');
export const automationTemplateResponseSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    version: z.string().describe('Template version (semver)'),
    visibility: z.nativeEnum(AutomationTemplateVisibilityEnum),
    feature: z.nativeEnum(ReachFeature).nullable(),
    featureSubType: automationFeatureSubTypeSchema.describe('Sub-type that qualifies the template within its feature, or null when not applicable'),
    contents: automationTemplateContents,
    tenants: automationTemplateTenantsSchema,
    createdAt: z.string().nullable(),
    updatedAt: z.string().nullable(),
});
export const automationTemplateListItemResponseSchema = automationTemplateResponseSchema.extend({
    activeBusinessIds: z
        .array(z.string().uuid())
        .describe('Tenants with an active automation created from this template'),
    categories: z.array(automationCategoryResponseSchema),
});
export var AutomationTemplateSyncStatusEnum;
(function (AutomationTemplateSyncStatusEnum) {
    AutomationTemplateSyncStatusEnum["PENDING"] = "pending";
    AutomationTemplateSyncStatusEnum["IN_PROGRESS"] = "in_progress";
    AutomationTemplateSyncStatusEnum["COMPLETED"] = "completed";
    AutomationTemplateSyncStatusEnum["FAILED"] = "failed";
})(AutomationTemplateSyncStatusEnum || (AutomationTemplateSyncStatusEnum = {}));
export const automationTemplateSyncStartedResponseSchema = z.object({
    jobId: z.string().uuid().describe('Poll Get Automation Template Sync with this ID'),
});
export const automationTemplateSyncResponseSchema = z.object({
    jobId: z.string().uuid(),
    status: z
        .nativeEnum(AutomationTemplateSyncStatusEnum)
        .describe('failed once the sync has finished and any tenant failed'),
    tenants: z
        .array(z.object({
        tenantId: z.string().uuid(),
        status: z
            .nativeEnum(AutomationTemplateSyncStatusEnum)
            .describe('failed when the template could not be applied to this tenant'),
    }))
        .describe('One entry per tenant the sync has reached so far'),
});
export function isReputationTemplateContents(contents) {
    return contents?.feature === ReachFeature.REPUTATION;
}
export const defaultAutomationTemplateMetadataSchema = z.object({
    name: z.string().describe('Template name identifier'),
    version: z.string().describe('Template version (semver)'),
    displayName: z.string().describe('Human-readable template name'),
    description: z.string().describe('Template description'),
});
export * from './reputation-generation.js';
/**
 * The seed templates' own `contents.name` is unreliable as a label — one of them is a raw
 * slug — so every surface names them by the role they play in the tenant's review flow.
 */
export const REPUTATION_DEFAULT_LABEL_BY_SUB_TYPE = {
    [ReputationFeatureSubTypeEnum.INTERNAL_REVIEW]: 'Internal Feedback Only',
    [ReputationFeatureSubTypeEnum.GOOGLE_REVIEW]: 'Google Review Only',
    [ReputationFeatureSubTypeEnum.POST_INTERNAL_REVIEW]: 'Google Review After Feedback',
    [ReputationFeatureSubTypeEnum.BOTH_REVIEWS]: 'Combined Review Request',
};
//# sourceMappingURL=automation-template.js.map